import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { itemAPI, uploadAPI } from '../services/api';
import { X, Upload, ArrowLeft } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';

const CATEGORIES = [
  'Clothes & Shoes',
  'Toys & Games',
  'Feeding',
  'Feeding (Non-liquid, Non-edible)',
  'Diapering',
  'Bathing & Skincare',
  'Bathing (Non-liquid)',
  'Health & Safety',
  'Nursery',
  'Strollers & Car Seats',
  'Carriers & Wraps',
  'Activity & Entertainment',
  'Books',
  'Blankets & Bedding',
  'Baby Gear',
  'Maternity',
  'Other',
];

const AGE_GROUPS = [
  'Newborn (0-3 months)',
  'Infant (3-12 months)',
  'Toddler (1-3 years)',
  'Preschool (3-5 years)',
  'All Ages',
];

const CONDITIONS = ['New with Tags', 'Like New', 'Good', 'Fair', 'Well Loved'];

const EditListing = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    ageGroup: '',
    condition: '',
    price: '',
    brand: '',
    color: '',
    location: '',
    tags: [],
    isForSale: true,
  });

  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [isDraft, setIsDraft] = useState(false);
  const [tagInput, setTagInput] = useState('');

  // Load item data
  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await itemAPI.getItem(id);
        const item = response.data.data;

        setFormData({
          title: item.title || '',
          description: item.description || '',
          category: item.category || '',
          ageGroup: item.ageGroup || '',
          condition: item.condition || '',
          price: item.price || '',
          brand: item.brand || '',
          color: item.color || '',
          location: item.location || '',
          tags: item.tags || [],
          isForSale: item.isForSale !== false,
        });

        setExistingImages(item.images || []);
        setIsDraft(item.status === 'draft');
      } catch (err) {
        setError('Failed to load item. You may not have permission to edit this item.');
        console.error('Failed to fetch item:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchItem();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);

    if (existingImages.length + newImages.length + files.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }

    setError(null);

    const imagePromises = files.map((file) => {
      return new Promise((resolve) => {
        resolve({
          file,
          preview: URL.createObjectURL(file),
          name: file.name,
        });
      });
    });

    const processedImages = await Promise.all(imagePromises);
    setNewImages((prev) => [...prev, ...processedImages]);
  };

  const removeExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      let imagesPayload = [...existingImages];

      // Upload new images if any
      if (newImages.length > 0) {
        const uploadResponse = await uploadAPI.uploadMultiple(
          newImages.map((img) => img.file)
        );
        imagesPayload = [...imagesPayload, ...uploadResponse.data.data];
      }

      // A draft needs at least one image before it can be published.
      if (isDraft && imagesPayload.length === 0) {
        setError('Please add at least one image to publish this listing.');
        setSaving(false);
        return;
      }

      const updatePayload = {
        ...formData,
        images: imagesPayload,
      };

      // Publish the draft once it has images.
      if (isDraft && imagesPayload.length > 0) {
        updatePayload.status = 'active';
      }

      // Update item
      await itemAPI.updateItem(id, updatePayload);

      navigate('/seller-dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update listing');
      console.error('Failed to update item:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error && !formData.title) {
    return (
      <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
        <p style={{ color: 'var(--error)', marginBottom: '20px' }}>{error}</p>
        <button onClick={() => navigate('/seller-dashboard')} className="btn btn-primary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '800px', paddingTop: '40px', paddingBottom: '40px' }}>
      <div style={{ marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button
          onClick={() => navigate('/seller-dashboard')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--textSecondary)',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          <ArrowLeft size={20} />
          Back
        </button>
        <h1 style={{ margin: 0 }}>{isDraft ? 'Finish Draft Listing' : 'Edit Listing'}</h1>
      </div>

      {isDraft && (
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#e3f2fd',
          borderLeft: '4px solid var(--primary)',
          borderRadius: '8px',
        }}>
          <strong>This item is a draft.</strong> Add at least one image, then click
          “Publish Listing” to make it visible to buyers.
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '8px',
            marginBottom: '20px',
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-body">
            <h3 style={{ marginBottom: '20px' }}>Basic Information</h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={5}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '16px',
                  }}
                >
                  <option value="">Select category</option>
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Age Group *
                </label>
                <select
                  name="ageGroup"
                  value={formData.ageGroup}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '16px',
                  }}
                >
                  <option value="">Select age group</option>
                  {AGE_GROUPS.map((age) => (
                    <option key={age} value={age}>
                      {age}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Condition *
                </label>
                <select
                  name="condition"
                  value={formData.condition}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '16px',
                  }}
                >
                  <option value="">Select condition</option>
                  {CONDITIONS.map((cond) => (
                    <option key={cond} value={cond}>
                      {cond}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Price ($) *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '16px',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Brand</label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '16px',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Color</label>
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '16px',
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="City, State"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Tags</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Add a tag and press Enter"
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '2px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '16px',
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="btn btn-secondary"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Add Tag
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      backgroundColor: 'var(--secondary)',
                      borderRadius: '16px',
                      fontSize: '14px',
                    }}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: '30px' }}>
          <div className="card-body">
            <h3 style={{ marginBottom: '20px' }}>Images</h3>

            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontWeight: '600', marginBottom: '12px' }}>Current Images:</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '16px' }}>
                  {existingImages.map((img, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={typeof img === 'string' ? img : (img?.thumbnail || img?.fullSize)}
                        alt={`Existing ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(index)}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Images */}
            {newImages.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontWeight: '600', marginBottom: '12px' }}>New Images to Upload:</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '16px' }}>
                  {newImages.map((img, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={img.preview}
                        alt={`New ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeNewImage(index)}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Button */}
            {existingImages.length + newImages.length < 5 && (
              <div>
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor="image-upload"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    padding: '40px',
                    border: '2px dashed var(--border)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    backgroundColor: 'var(--secondary)',
                  }}
                >
                  <Upload size={32} color="var(--textSecondary)" />
                  <span style={{ color: 'var(--textSecondary)' }}>
                    Click to add more images ({existingImages.length + newImages.length}/5)
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => navigate('/seller-dashboard')}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : isDraft ? 'Publish Listing' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditListing;
